/**
 * Investor Drawdown Service
 *
 * Handles investor-specific drawdown operations for the portal
 */

import { investorPortalApiClient } from '../../shared/utils/api-client';
import type { DrawdownPayment } from '@/types/drawdown';

interface DrawdownBatch {
  id: string;
  batch_number: number;
  notice_date: string;
  due_date: string;
  total_amount: number;
  investor_count: number;
  status: 'DRAFT' | 'ISSUED' | 'SENT' | 'COMPLETED';
}

interface ApiInvestorDrawdownAllocation {
  drawdown_investor_id?: string;
  drawdown_amount: string;
  amount_paid: string;
  amount_due: string;
  status: 'pending' | 'partial' | 'paid' | 'overdue' | 'exempted';
  days_overdue: number;
  prepayment_used: string;
  net_amount_due: string;
  prepayment_contribution: string;
  invoice_amount: string;
  batch_id?: string;
  batch_number?: number;
  payment_count?: number;
  first_payment_date?: string;
  last_payment_date?: string;
}

interface ApiInvestorDrawdownSummary extends Omit<
  InvestorDrawdownSummary,
  'total_amount' | 'investor_allocation'
> {
  total_amount: string;
  investor_allocation: ApiInvestorDrawdownAllocation;
}

export interface InvestorDrawdownSummary {
  id: string;
  fund_id: string;
  fund_name: string;
  drawdown_number: number;
  total_amount: number;
  currency: string;
  issue_date: string;
  due_date: string;
  purpose: string;
  status: 'draft' | 'issued' | 'partial' | 'completed' | 'cancelled';
  batch_count: number;
  batches: DrawdownBatch[];
  investor_allocation: {
    drawdown_investor_id?: string;
    drawdown_amount: number;
    amount_paid: number;
    amount_due: number;
    status: 'pending' | 'partial' | 'paid' | 'overdue' | 'exempted';
    days_overdue: number;
    prepayment_used: number;
    net_amount_due: number;
    prepayment_contribution: number;
    invoice_amount: number;
    batch_id?: string;
    batch_number?: number;
    payment_count?: number;
    first_payment_date?: string;
    last_payment_date?: string;
  };
  notice_document_id?: string;
  created_at: string;
  updated_at: string;
}

function mapInvestorDrawdownSummary(
  raw: ApiInvestorDrawdownSummary
): InvestorDrawdownSummary {
  const a = raw.investor_allocation;
  return {
    ...raw,
    total_amount: Number(raw.total_amount),
    investor_allocation: {
      ...a,
      drawdown_amount: Number(a.drawdown_amount),
      amount_paid: Number(a.amount_paid),
      amount_due: Number(a.amount_due),
      prepayment_used: Number(a.prepayment_used),
      net_amount_due: Number(a.net_amount_due),
      prepayment_contribution: Number(a.prepayment_contribution),
      invoice_amount: Number(a.invoice_amount),
    },
  };
}

interface PrepaymentSourceTransfer {
  id: string;
  counterparty_name: string | null;
  transfer_date: string;
  amount: number;
  currency: string;
}

export interface PrepaymentSource {
  amount: number;
  drawdown_id: string;
  is_viewable: boolean;
  drawdown_number: number;
  issue_date: string;
  is_settled: boolean;
  bank_transfers: PrepaymentSourceTransfer[];
}

type ApiPrepaymentSource = Omit<
  PrepaymentSource,
  'amount' | 'bank_transfers'
> & {
  amount: string;
  bank_transfers: (Omit<PrepaymentSourceTransfer, 'amount'> & {
    amount: string;
  })[];
};

interface ApiInvestorDrawdownDetail extends ApiInvestorDrawdownSummary {
  prepayment_sources: ApiPrepaymentSource[];
  prepayment_unattributed: string;
}

function mapPrepaymentSource(raw: ApiPrepaymentSource): PrepaymentSource {
  return {
    ...raw,
    amount: Number(raw.amount),
    bank_transfers: raw.bank_transfers.map((transfer) => ({
      ...transfer,
      amount: Number(transfer.amount),
    })),
  };
}

export interface InvestorDrawdownDetail extends InvestorDrawdownSummary {
  description?: string;
  has_prepayment: boolean;
  prepayment_amount?: number;
  investor_payments: DrawdownPayment[];
  payment_instructions?: {
    bank_name: string;
    account_name: string;
    iban: string;
    bic: string;
    reference: string | null;
  };
  prepayment_sources: PrepaymentSource[];
  prepayment_unattributed: number;
}

export class InvestorDrawdownService {
  /**
   * Get all drawdowns for the current investor
   */
  static async getInvestorDrawdowns(
    params: {
      status?: 'pending' | 'partial' | 'paid' | 'overdue' | 'all';
      limit?: number;
      skip?: number;
    } = {}
  ): Promise<InvestorDrawdownSummary[]> {
    const queryParams: Record<string, string> = {};

    if (params.status && params.status !== 'all') {
      queryParams.status = params.status;
    }
    if (params.limit) {
      queryParams.limit = params.limit.toString();
    }
    if (params.skip) {
      queryParams.skip = params.skip.toString();
    }

    const response = await investorPortalApiClient.get<{
      drawdowns: ApiInvestorDrawdownSummary[];
      total: number;
    }>('/portal/transactions/drawdowns', {
      params: queryParams,
    });

    return response.drawdowns.map(mapInvestorDrawdownSummary);
  }

  static async getInvestorDrawdownDetail(
    drawdownId: string
  ): Promise<InvestorDrawdownDetail> {
    const drawdown =
      await investorPortalApiClient.get<ApiInvestorDrawdownDetail>(
        `/portal/transactions/drawdowns/${drawdownId}`
      );

    return {
      ...(mapInvestorDrawdownSummary(drawdown) as InvestorDrawdownDetail),
      prepayment_sources: drawdown.prepayment_sources.map(mapPrepaymentSource),
      prepayment_unattributed: Number(drawdown.prepayment_unattributed),
    };
  }

  /**
   * Get unpaid drawdowns for the current investor (for task overview)
   */
  static async getUnpaidDrawdowns(): Promise<InvestorDrawdownSummary[]> {
    return this.getInvestorDrawdowns({ status: 'pending' });
  }

  /**
   * Get overdue drawdowns for the current investor
   */
  static async getOverdueDrawdowns(): Promise<InvestorDrawdownSummary[]> {
    return this.getInvestorDrawdowns({ status: 'overdue' });
  }

  /**
   * Mark a drawdown as paid (for simulation purposes)
   */
  static async markDrawdownAsPaid(
    drawdownId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      // In a real implementation, this would record a payment
      console.log(`Marking drawdown ${drawdownId} as paid`);
      return {
        success: true,
        message: 'Payment recorded successfully',
      };
    } catch (error) {
      console.error('Error marking drawdown as paid:', error);
      throw error;
    }
  }
}
